<script>
  import { onMount, onDestroy } from 'svelte';
  import { api } from '../lib/api.js';

  // Live ad visits — one row per paid click, from the cookieless beacon log (/api/analytics/visits).
  // Polls every 20 s while the tab is visible.
  let data = null, loading = true, error = null;
  let range = 'today', source = 'all', hideBounces = false, expanded = null;
  let timer = null;

  async function load() {
    loading = true; error = null;
    try { data = await api(`/analytics/visits?range=${range}`); }
    catch (e) { error = e.message; } finally { loading = false; }
  }
  function setRange(r) { range = r; expanded = null; load(); }
  onMount(() => {
    load();
    timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 20000);
  });
  onDestroy(() => clearInterval(timer));

  const STAGE_LABEL = { landed: 'landed', product: 'product page', builder: 'opened builder', personalised: 'personalised',
    checkout_try: 'tried add to cart', cart: 'in basket', ordered: 'ordered' };
  const STEP_LABEL = { open: 'opened builder', design: 'changed design', card_opened: 'opened card', write_cue: 'tapped “Write your message”', message: 'typed message', long_message: 'long message (card will scroll)',
    handwriting: 'uploaded handwriting', delivery: 'recipient details', atc_try: 'add to cart', atc_blocked: 'blocked',
    atc_error: 'save error', cart: 'sent to basket' };
  const fmtClock = (s) => (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—');
  const fmtWhen = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    const today = new Date().toDateString() === d.toDateString();
    return today ? fmtClock(s) : d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const fmtDur = (ms) => { if (!ms) return '—'; const s = Math.round(ms / 1000); return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`; };
  const ago = (s) => { const m = Math.round((Date.now() - new Date(s)) / 60000); return m < 1 ? 'now' : m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`; };
  const isLive = (v) => Date.now() - new Date(v.lastAt) < 5 * 60000;
  const shortPath = (p) => (p || '').replace(/^\/collections\/[^/]+\/products\//, '/products/').replace(/^\/products\//, '').replace(/^\/collections\//, 'coll: ') || '/';

  $: rows = data ? data.visits.filter((v) => (source === 'all' || v.source === source) && (!hideBounces || !v.bounced)) : [];
  $: sums = data ? (source === 'all' ? data.totals : (data.bySource.find((s) => s.source === source) || null)) : null;
  $: devRows = data ? (source === 'all' ? data.byDevice : (data.byDeviceSource || []).filter((d) => d.source === source)) : [];
  $: kwRows = data ? data.byKeyword.filter((k) => source === 'all' || k.source === source) : [];
  $: liveNow = data ? data.visits.filter((v) => !v.bot && isLive(v)).length : 0;
  const funnelPct = (s, stage) => (s ? (s.funnel.find((f) => f.stage === stage) || {}).pct : null);
</script>

<section>
  <div class="head">
    <h2>Live ad visits <small>paid clicks only · cookieless · updates every 20 s</small></h2>
    <div class="ctrl">
      {#each [['today', 'Today'], ['24h', '24 h'], ['7d', '7 days'], ['28d', '28 days']] as [r, l]}
        <button class:on={range === r} on:click={() => setRange(r)}>{l}</button>
      {/each}
      <select bind:value={source}><option value="all">All sources</option><option>Google</option><option>Bing</option></select>
      <label><input type="checkbox" bind:checked={hideBounces} /> hide bounces</label>
      <span class="meta">{#if loading}loading…{:else if data}updated {fmtClock(data.generatedAt)}{/if}</span>
    </div>
  </div>

  {#if error}<p class="error">{error}</p>{/if}

  {#if data && sums}
    <div class="cards">
      <div class="card"><span class="label">Ad visits</span><span class="value">{sums.visits}</span><span class="sub">{liveNow} active in the last 5 min{#if data.bots} · {data.bots} bot{data.bots === 1 ? '' : 's'} excluded{/if}</span></div>
      <div class="card"><span class="label">Bounced</span><span class="value" class:bad={sums.bounceRate >= 60}>{sums.bounceRate ?? '—'}%</span><span class="sub">1 page, no builder, under 10 s</span></div>
      <div class="card"><span class="label">Median pages · time</span><span class="value">{sums.medianPages ?? '—'} · {sums.medianActiveSec != null ? fmtDur(sums.medianActiveSec * 1000) : '—'}</span><span class="sub">store pages; time = tab visible</span></div>
      <div class="card"><span class="label">Opened builder</span><span class="value">{funnelPct(sums, 'builder') ?? '—'}%</span><span class="sub">personalised {funnelPct(sums, 'personalised') ?? '—'}% · basket {funnelPct(sums, 'cart') ?? '—'}%</span></div>
      <div class="card"><span class="label">Ordered</span><span class="value">{sums.orders}</span><span class="sub">{sums.visits ? Math.round(sums.orders / sums.visits * 1000) / 10 : 0}% of visits</span></div>
    </div>

    <div class="funnel">
      {#each sums.funnel as f}
        <div class="fstep" title={`${f.count} of ${sums.visits}`}>
          <div class="fbar"><div class="ffill" style={`width:${f.pct || 0}%`}></div></div>
          <span class="flab">{STAGE_LABEL[f.stage]}</span><span class="fnum">{f.count} <small>{f.pct ?? 0}%</small></span>
        </div>
      {/each}
    </div>

    {#if data.bySource.length > 1 && source === 'all'}
      <div class="tablewrap"><table class="mini">
        <thead><tr><th>Source</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Med. time</th><th class="num">Builder</th><th class="num">Basket</th><th class="num">Orders</th></tr></thead>
        <tbody>
          {#each data.bySource as s}
            <tr><td>{s.source}</td><td class="num">{s.visits}</td><td class="num">{s.bounceRate}%</td><td class="num">{s.medianPages}</td><td class="num">{fmtDur(s.medianActiveSec * 1000)}</td><td class="num">{funnelPct(s, 'builder')}%</td><td class="num">{funnelPct(s, 'cart')}%</td><td class="num">{s.orders}</td></tr>
          {/each}
        </tbody>
      </table></div>
    {/if}

    {#if devRows.length}
      <h3>By device <small>{source === 'all' ? 'all sources' : source} · same range · bots excluded</small></h3>
      <div class="tablewrap"><table class="mini">
        <thead><tr><th>Device</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Med. time</th><th class="num">Builder</th><th class="num">Personalised</th><th class="num">Basket</th><th class="num">Orders</th><th class="num">Visit → order</th></tr></thead>
        <tbody>
          {#each devRows as d}
            <tr><td>{d.device === 'm' ? 'Phone' : 'Desktop'}</td><td class="num">{d.visits}</td><td class="num">{d.bounceRate}%</td><td class="num">{d.medianPages}</td><td class="num">{fmtDur(d.medianActiveSec * 1000)}</td><td class="num">{funnelPct(d, 'builder')}%</td><td class="num">{funnelPct(d, 'personalised')}%</td><td class="num">{funnelPct(d, 'cart')}%</td><td class="num">{d.orders}</td><td class="num">{d.visits ? (d.orders / d.visits * 100).toFixed(1) : '—'}%</td></tr>
          {/each}
        </tbody>
      </table></div>
      <p class="sub-note">Phone = screen under 768px wide. Phones undercount: someone who browses on a phone and buys later on a computer shows up here as a phone visit with no order, because the ad tag doesn't cross devices. Treat a gap as real only once each device has 100+ visits.</p>
    {/if}

    <h3>Visits</h3>
    {#if !rows.length}
      <p class="empty">No ad visits {range === 'today' ? 'yet today' : 'in this range'}.</p>
    {:else}
      <div class="tablewrap">
        <table class="visits">
          <thead><tr><th>Click</th><th>Ad · device</th><th>Keyword / query</th><th>Landed on</th><th class="num">Pages</th><th class="num">Time</th><th class="num">Scroll</th><th>Furthest</th><th>Last seen</th></tr></thead>
          <tbody>
            {#each rows as v (v.id)}
              <tr class:bounced={v.bounced} class:ordered={v.order} class:bot={v.bot} on:click={() => (expanded = expanded === v.id ? null : v.id)}>
                <td class="t">{fmtWhen(v.firstAt)}</td>
                <td class="t"><span class={`src ${v.source.toLowerCase()}`}>{v.source}</span>{#if v.device}<br /><small class="grp">{v.device === 'm' ? 'phone' : 'desktop'}</small>{/if}</td>
                <td class="kw">{v.keyword || '—'}{#if v.matchType}<span class="chip">{v.matchType}</span>{/if}{#if v.query && v.query !== v.keyword}<br /><small class="grp">“{v.query}”</small>{/if}</td>
                <td class="path" title={v.landing}>{shortPath(v.landing)}</td>
                <td class="num">{v.pages}</td>
                <td class="num">{fmtDur(v.activeMs)}</td>
                <td class="num">{v.maxScroll ? `${v.maxScroll}%` : '—'}</td>
                <td><span class={`stage s${v.stageIndex}`}>{v.order ? `ordered ${v.order.number}` : STAGE_LABEL[v.stage]}</span>{#if v.bounced}<span class="chip bad">bounce</span>{/if}{#if v.blocked.length}<span class="chip warn" title="add-to-cart blocked on">blocked: {v.blocked.join(', ')}</span>{/if}{#if v.bot}<span class="chip">bot</span>{/if}</td>
                <td class="t">{#if isLive(v)}<span class="live">● live</span>{:else}{ago(v.lastAt)}{/if}</td>
              </tr>
              {#if expanded === v.id}
                <tr class="detail"><td colspan="9">
                  <ol class="trail">
                    {#each [...v.views.map((x) => ({ at: x.at, kind: 'view', x })), ...v.stepLog.map((x) => ({ at: x.at, kind: 'step', x }))].sort((a, b) => a.at.localeCompare(b.at)) as e}
                      <li>
                        <span class="d">{new Date(e.at).toLocaleTimeString('en-GB')}</span>
                        {#if e.kind === 'view'}
                          <b>{e.x.type === 'builder' ? 'builder' : e.x.path}</b> <small class="grp">{e.x.type}{e.x.ms ? ` · ${fmtDur(e.x.ms)}` : ''}{e.x.scroll ? ` · scrolled ${e.x.scroll}%` : ''}</small>
                        {:else}
                          → {STEP_LABEL[e.x.step] || e.x.step}{#if e.x.reason} <small class="grp">({e.x.reason})</small>{/if}
                        {/if}
                      </li>
                    {/each}
                    {#if v.order}<li><span class="d">{new Date(v.order.at).toLocaleTimeString('en-GB')}</span> <b>ordered {v.order.number}</b>{#if v.order.total} <small class="grp">£{v.order.total.toFixed(2)}</small>{/if}</li>{/if}
                  </ol>
                  <small class="grp">Clicked {v.clickedAt ? new Date(v.clickedAt).toLocaleString('en-GB') : '—'}{#if v.campaign} · campaign {v.campaign}{/if}</small>
                </td></tr>
              {/if}
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    {#if kwRows.length}
      <h3>By keyword <small>same range · bots excluded</small></h3>
      <div class="tablewrap">
        <table class="mini">
          <thead><tr><th>Keyword</th><th>Ad</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Med. time</th><th class="num">Product</th><th class="num">Builder</th><th class="num">Personalised</th><th class="num">Basket</th><th class="num">Orders</th></tr></thead>
          <tbody>
            {#each kwRows as k}
              <tr>
                <td>{k.keyword || '(no keyword)'}</td><td><span class={`src ${k.source.toLowerCase()}`}>{k.source}</span></td>
                <td class="num">{k.visits}</td><td class="num" class:bad={k.visits >= 3 && k.bounceRate >= 70}>{k.bounceRate}%</td>
                <td class="num">{k.medianPages}</td><td class="num">{fmtDur(k.medianActiveSec * 1000)}</td>
                <td class="num">{funnelPct(k, 'product')}%</td><td class="num">{funnelPct(k, 'builder')}%</td><td class="num">{funnelPct(k, 'personalised')}%</td><td class="num">{funnelPct(k, 'cart')}%</td>
                <td class="num" class:good={k.orders}>{k.orders}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    <p class="sub-note">Recorded only for visitors who arrive from a paid ad, and only for as long as they carry the ad tag. Clicking a link on the site keeps the tag; typing a URL or coming back later from a bookmark loses it. Nothing is stored on the visitor's device, and no click id, IP address or browser details are kept. A visit is one ad click. Refreshing or going back to the landing page stays in the same visit. Time counts only while the tab is visible and is reported when the visitor leaves or switches tab, so a visit marked live may still show — for time. "Ordered" is set by the order webhook from the order's ad tag.{#if data.truncated} Showing the newest {data.visits.length} visits only.{/if}</p>
  {:else if loading}
    <p class="empty">Loading…</p>
  {/if}
</section>

<style>
  section { padding: 1rem 1.25rem 3rem; }
  .head { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: .5rem; }
  h2 { margin: 0; font-size: 1.2rem; }
  h2 small, h3 small { font-weight: 400; color: #888; font-size: .75rem; }
  h3 { margin: 1.4rem 0 .5rem; font-size: 1rem; }
  .ctrl { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; font-size: .82rem; }
  .ctrl button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .2rem .6rem; border-radius: 999px; font-size: .78rem; }
  .ctrl button.on { background: #2a5db0; color: #fff; border-color: #2a5db0; }
  .ctrl select { font: inherit; font-size: .8rem; padding: .15rem .3rem; border: 1px solid #ccc; border-radius: 6px; }
  .meta { color: #888; margin-left: .5rem; }
  .error { color: #b00020; }
  .empty { color: #888; }
  .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: .75rem; margin: 1rem 0; }
  .card { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .7rem .9rem; display: flex; flex-direction: column; gap: .15rem; }
  .card .label { font-size: .75rem; color: #888; text-transform: uppercase; letter-spacing: .03em; }
  .card .value { font-size: 1.5rem; font-weight: 600; font-variant-numeric: tabular-nums; }
  .card .value.bad { color: #b00020; }
  .card .sub { font-size: .75rem; color: #888; }
  .funnel { display: grid; grid-template-columns: repeat(7, 1fr); gap: .5rem; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .7rem; }
  @media (max-width: 800px) { .funnel { grid-template-columns: repeat(2, 1fr); } }
  .fstep { display: flex; flex-direction: column; gap: .2rem; font-size: .75rem; }
  .fbar { height: 8px; background: #eef2f8; border-radius: 4px; overflow: hidden; }
  .ffill { height: 100%; background: #4a7dbd; }
  .flab { color: #666; }
  .fnum { font-weight: 600; font-variant-numeric: tabular-nums; }
  .fnum small { color: #888; font-weight: 400; }
  .tablewrap { overflow-x: auto; }
  table { border-collapse: collapse; width: 100%; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; font-size: .82rem; }
  th { text-align: left; font-weight: 600; color: #666; font-size: .75rem; padding: .4rem .5rem; border-bottom: 1px solid #e5e5e5; }
  td { padding: .35rem .5rem; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
  table.mini { margin-top: .75rem; }
  table.visits tbody tr:not(.detail) { cursor: pointer; }
  table.visits tbody tr:not(.detail):hover td { background: #f7f9fc; }
  tr.bounced td { color: #999; }
  tr.ordered td { background: #f0faf2; }
  tr.detail td { background: #fafafa; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  td.good { color: #1c7a2e; font-weight: 600; }
  td.bad { color: #b00020; font-weight: 600; }
  .t { white-space: nowrap; font-variant-numeric: tabular-nums; }
  td.kw { max-width: 260px; }
  td.path { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .src { display: inline-block; padding: .05rem .45rem; border-radius: 4px; font-weight: 700; font-size: .72rem; color: #fff; background: #999; }
  .src.google { background: #1a73e8; }
  .src.bing { background: #008373; }
  .chip { font-size: .66rem; padding: .05rem .4rem; border-radius: 999px; background: #f0f0f0; color: #888; margin-left: .4rem; white-space: nowrap; }
  .chip.bad { background: #fde8e8; color: #b00020; }
  .chip.warn { background: #fff3e0; color: #b26a00; }
  .grp { color: #999; font-size: .72rem; }
  .stage { font-size: .75rem; padding: .05rem .45rem; border-radius: 999px; background: #f0f0f0; color: #666; white-space: nowrap; }
  .stage.s1 { background: #eef2f8; color: #2a5db0; }
  .stage.s2, .stage.s3 { background: #e8f0fe; color: #2a5db0; font-weight: 600; }
  .stage.s4, .stage.s5 { background: #fff3e0; color: #b26a00; font-weight: 600; }
  .stage.s6 { background: #e2f5e5; color: #1c7a2e; font-weight: 700; }
  .live { color: #1c7a2e; font-weight: 600; }
  .trail { margin: .2rem 0 .4rem; padding-left: 1.2rem; font-size: .8rem; }
  .trail li { padding: .1rem 0; }
  .trail .d { color: #888; font-variant-numeric: tabular-nums; margin-right: .4rem; }
  .sub-note { font-size: .75rem; color: #888; margin-top: 1rem; max-width: 70em; }
</style>
